import React, { useEffect, useState, useMemo } from "react";
import { useHistoryBack } from "../lib/useHistoryBack";
import { VolverButton } from "../components/VolverButton";
import { useParams, Link } from "react-router-dom";
import api, { formatMoney, formatNumber } from "../lib/api";
import {
  Loader2, ArrowLeft, Truck, ShoppingCart, AlertTriangle, RefreshCcw, Save,
  CheckCircle2, ChevronDown, ChevronUp, Calendar, Sparkles, ExternalLink, X,
} from "lucide-react";
import { useSortable, SortHeader } from "../lib/sort";
import { ExportButtons } from "../lib/exports";

const QUICK_HORIZONS = [15, 30, 45, 60, 90, 120];

const fmtD = (v) => (v ? new Date(v).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

export default function SupplierOrder() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [studies, setStudies] = useState([]);
  const [studyId, setStudyId] = useState("");
  const [cfg, setCfg] = useState({ is_direct: false, days_payment: "", discount_pct: "", min_order_eur: "", frequency_days: "", transport_cost: "" });
  const [showCfg, setShowCfg] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [horizon, setHorizon] = useState(60);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: s }, { data: st }] = await Promise.all([
          api.get(`/suppliers/${id}`),
          api.get("/studies"),
        ]);
        setSupplier(s);
        setCfg({
          is_direct: !!s.is_direct,
          days_payment: s.days_payment ?? "",
          discount_pct: s.discount_pct ?? "",
          min_order_eur: s.min_order_eur ?? "",
          frequency_days: s.frequency_days ?? "",
          transport_cost: s.transport_cost ?? "",
        });
        // Siempre usar el estudio más reciente por fecha de creación
        const sorted = [...st].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        setStudies(sorted);
        if (sorted.length) setStudyId(sorted[0].id);
      } finally { setLoading(false); }
    })();
  }, [id]);

  const saveConfig = async () => {
    const payload = {
      is_direct: cfg.is_direct,
      days_payment: cfg.days_payment === "" ? null : Number(cfg.days_payment),
      discount_pct: cfg.discount_pct === "" ? null : Number(cfg.discount_pct),
      min_order_eur: cfg.min_order_eur === "" ? null : Number(cfg.min_order_eur),
      frequency_days: cfg.frequency_days === "" ? null : Number(cfg.frequency_days),
      transport_cost: cfg.transport_cost === "" ? null : Number(cfg.transport_cost),
    };
    const { data } = await api.patch(`/suppliers/${id}/config`, payload);
    setSupplier(data);
    setSavedMsg("Guardado");
    setTimeout(() => setSavedMsg(""), 2000);
  };

  const generate = async (overrideStudyId, overrideHorizon) => {
    const sid_study = overrideStudyId || studyId;
    const h = overrideHorizon || horizon;
    if (!sid_study || !h) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/suppliers/${id}/order-builder`, {
        study_id: sid_study,
        horizon_days: Number(h),
      });
      setProposal(data);
      setTimeout(() => {
        document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } finally { setGenerating(false); }
  };

  // Auto-generate on first load (with default horizon = 60d)
  useEffect(() => {
    if (studyId && !proposal && !generating) {
      generate(studyId, 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId]);

  const [ajPed, setAjPed] = useState({});
  useEffect(() => { setAjPed({}); }, [proposal]);
  const ord = useMemo(() => {
    const o = proposal?.order;
    if (!o) return null;
    const disc = (o.discount_pct || 0) / 100;
    const items = o.items.map((it) => {
      const pedido = ajPed[it.cn] ?? it.pedido;
      const coste = Math.round(pedido * (it.pmc || 0) * 100) / 100;
      return { ...it, pedido, propuesto: it.pedido, coste, coste_propuesto: it.coste, neto: Math.round(coste * (1 - disc) * 100) / 100 };
    });
    const activos = items.filter((i) => i.pedido > 0);
    const total_net = activos.reduce((t, i) => t + i.neto, 0);
    const min = proposal?.supplier?.min_order_eur;
    return { ...o, items, lines: activos.length, units: activos.reduce((t, i) => t + i.pedido, 0),
             total_cost: activos.reduce((t, i) => t + i.coste, 0), total_net, meets_minimum: min == null || total_net >= min };
  }, [proposal, ajPed]);
  const orderSort = useSortable(ord?.items || [], "coste_propuesto", "desc");
  const paradoSort = useSortable(proposal?.parado?.items || [], "valor_pvp", "desc");

  // Family parados panel
  const [famPanel, setFamPanel] = useState(null); // { familia, items, count, total_value_pvp }
  const famNav = useHistoryBack(!!famPanel, () => setFamPanel(null));
  const [famLoading, setFamLoading] = useState(false);
  const openFamily = async (familia) => {
    if (!familia || !studyId) return;
    setFamLoading(true);
    setFamPanel({ familia, items: [], count: 0, total_value_pvp: 0 });
    try {
      const { data } = await api.get(`/studies/${studyId}/family-parados`, { params: { familia } });
      setFamPanel(data);
    } finally { setFamLoading(false); }
  };
  const famSort = useSortable(famPanel?.items || [], "valor_pvp", "desc");

  if (loading) return <div className="p-8 flex gap-2 text-slate-500"><Loader2 className="animate-spin" size={16}/> Cargando…</div>;
  if (!supplier) return <div className="p-8 text-red-600">Proveedor no encontrado.</div>;

  return (
    <div className="p-8 fade-in max-w-7xl mx-auto" data-testid="supplier-order-page">
      <Link to="/reaprovisionamientos" className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-3">
        <ArrowLeft size={12}/> Volver a Reaprovisionamientos
      </Link>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Asistente de pedido</div>
      <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-1 flex items-center gap-3">
        <Truck size={28} className="text-slate-400" /> {supplier.name}
      </h1>
      <p className="text-slate-600 mb-3">Generaremos en un clic el pedido sugerido <b>y</b> el listado de stock parado (CC + D) de este proveedor para canjear con el comercial.</p>

      {/* Badge: estudio más reciente (clic para abrir detalle en nueva pestaña) */}
      {studies.length > 0 && (
        <a
          href={`/studies/${studies[0].id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 transition rounded-full text-xs cursor-pointer group"
          data-testid="study-badge"
          title="Abrir detalle del estudio en una pestaña nueva"
        >
          <span className="text-slate-500 font-semibold uppercase tracking-widest">Estudio:</span>
          <span className="font-bold text-slate-900 group-hover:underline">{studies[0].name}</span>
          <span className="text-emerald-600 font-semibold">· más reciente</span>
          <ExternalLink size={11} className="text-slate-400 group-hover:text-slate-700"/>
        </a>
      )}

      {/* Paso único: Horizonte */}
      <div className="card p-6 mb-4 border-2 border-slate-900" data-testid="step-horizon">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Paso único</div>
        <h2 className="font-display text-2xl font-extrabold mb-1 flex items-center gap-2">
          <Calendar size={22} className="text-slate-400"/> ¿Para cuántos días quieres pedir?
        </h2>
        <p className="text-slate-500 text-sm mb-4">Calcularemos las unidades necesarias para cubrir ese horizonte según la rotación real de cada producto.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {QUICK_HORIZONS.map((d) => (
            <button key={d} onClick={() => { setHorizon(d); generate(studyId, d); }}
              className={"px-5 py-3 rounded-xl text-sm font-bold border-2 transition " +
                (Number(horizon) === d
                  ? "bg-slate-900 text-white border-slate-900 shadow-lg"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}
              data-testid={`horizon-${d}`}>
              {d} días
            </button>
          ))}
          <div className="flex items-center gap-2 ml-2">
            <span className="text-sm text-slate-500">o</span>
            <input type="number" min="1" value={horizon} onChange={(e) => setHorizon(e.target.value)}
              className="input w-24" data-testid="horizon-custom"/>
            <span className="text-sm text-slate-500">días</span>
          </div>
        </div>
        <button onClick={() => generate()} disabled={generating || !studyId} className="btn btn-primary text-base px-6 py-3" data-testid="generate-order">
          {generating
            ? <><Loader2 size={16} className="animate-spin"/> Calculando…</>
            : <><Sparkles size={16}/> Recalcular</>}
        </button>
      </div>

      {/* Configuración comercial — colapsable */}
      <div className="card mb-6" data-testid="supplier-config">
        <button onClick={() => setShowCfg(!showCfg)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition" data-testid="cfg-toggle">
          <div className="text-left">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Opcional</div>
            <div className="font-display font-bold text-base">Configuración comercial del proveedor</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {supplier.is_direct ? "Directo" : "Mayorista"}
              {supplier.discount_pct != null && ` · ${supplier.discount_pct}% dto`}
              {supplier.days_payment != null && ` · ${supplier.days_payment}d pago`}
              {supplier.min_order_eur != null && ` · min ${formatMoney(supplier.min_order_eur)}`}
            </div>
          </div>
          {showCfg ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
        </button>
        {showCfg && (
          <div className="px-5 pb-5 border-t border-slate-100 pt-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
              <label className="col-span-2 md:col-span-1 flex items-center gap-2 cursor-pointer" data-testid="cfg-direct">
                <input type="checkbox" checked={cfg.is_direct} onChange={(e) => setCfg({...cfg, is_direct: e.target.checked})}/>
                <span className="text-sm font-semibold">Directo</span>
              </label>
              {[
                ["days_payment", "Días pago"],
                ["discount_pct", "Descuento %"],
                ["min_order_eur", "Pedido mínimo €"],
                ["frequency_days", "Frecuencia (d)"],
                ["transport_cost", "Transporte €"],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{lbl}</label>
                  <input type="number" step="0.01" value={cfg[k]} onChange={(e) => setCfg({...cfg, [k]: e.target.value})}
                    className="input mt-0.5" data-testid={`cfg-${k}`}/>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={saveConfig} className="btn btn-outline btn-sm" data-testid="cfg-save"><Save size={13}/> Guardar</button>
              {savedMsg && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12}/> {savedMsg}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Resultados */}
      {proposal && (
        <div id="results">
          {/* Resumen */}
          <div className="card p-5 mb-5 !bg-slate-900 text-white">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Resultado</div>
                <h2 className="font-display text-2xl font-extrabold">Pedido a {proposal.horizon_days} días</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Líneas</div><div className="font-display text-lg font-bold">{ord.lines}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Unidades</div><div className="font-display text-lg font-bold">{formatNumber(ord.units)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Coste bruto</div><div className="font-display text-lg font-bold">{formatMoney(ord.total_cost)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Descuento</div><div className="font-display text-lg font-bold">{(proposal.order.discount_pct || 0).toFixed(1)}%</div></div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Neto a pagar</div>
                <div className={"font-display text-lg font-bold " + (ord.meets_minimum ? "text-emerald-400" : "text-red-400")}>
                  {formatMoney(ord.total_net)}
                </div>
                {!ord.meets_minimum && <div className="text-[10px] text-red-300 flex items-center gap-1 mt-1"><AlertTriangle size={10}/> No alcanza el mínimo</div>}
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-700 flex flex-wrap items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <RefreshCcw size={14} className="text-amber-400"/>
                <span className="text-slate-300">Stock parado de {supplier.name} para canje:</span>
                <span className="font-bold text-amber-300">{formatMoney(proposal.parado.total_value_pvp)}</span>
                <span className="text-slate-400">({proposal.parado.lines} ref.)</span>
              </div>
            </div>
          </div>

          {/* PEDIDO */}
          <div className="card mb-6">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-display text-lg font-bold flex items-center gap-2">
                <ShoppingCart size={18} className="text-emerald-600"/> Pedido sugerido ({ord.lines} líneas)
              </h2>
              <ExportButtons
                filename={`pedido-${supplier.name}-${proposal.horizon_days}d`}
                title={`Pedido directo · ${supplier.name} · ${proposal.horizon_days} días`}
                subtitle={`${ord.lines} líneas · ${ord.units} uds · Neto ${formatMoney(ord.total_net)}`}
                sheetName="Pedido"
                orientation="landscape"
                columns={[
                  { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                  { header: "Familia", key: "familia" },
                  { header: "Productos en familia", key: "family_total", align: "right" },
                  { header: "Parados en familia", key: "family_parado_count", align: "right" },
                  { header: "ABCD", key: "abcd" }, { header: "Stock", key: "stock", align: "right" },
                  { header: "V.diaria", key: "venta_diaria", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Horizonte", key: "horizonte_dias", align: "right" },
                  { header: "Objetivo", key: "objetivo", align: "right" },
                  { header: "Uds vendidas", key: "uds_periodo", align: "right" },
                  { header: "Última venta", key: "ultima_venta", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Última entrada", key: "ultima_entrada", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Pedido", key: "pedido", align: "right" },
                  { header: "PMC", key: "pmc", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Coste", key: "coste", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Neto", key: "neto", format: (v) => (v||0).toFixed(2), align: "right" },
                ]}
                rows={orderSort.sortedRows.filter((r) => r.pedido > 0)}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="dense text-[11px]" data-testid="order-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={orderSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={orderSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Familia · análisis" k="familia" sort={orderSort} defaultDir="asc"/></th>
                    <th><SortHeader label="ABCD" k="abcd" sort={orderSort} defaultDir="asc"/></th>
                    <th className="num"><SortHeader label="Stock" k="stock" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Uds vend." k="uds_periodo" sort={orderSort} num/></th>
                    <th><SortHeader label="Últ. venta" k="ultima_venta" sort={orderSort} defaultDir="desc"/></th>
                    <th><SortHeader label="Últ. entrada" k="ultima_entrada" sort={orderSort} defaultDir="desc"/></th>
                    <th className="num"><SortHeader label="V/día" k="venta_diaria" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Objetivo" k="objetivo" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Pedido" k="pedido" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="PMC" k="pmc" sort={orderSort} num/></th>
                    <th className="num"><SortHeader label="Neto" k="neto" sort={orderSort} num/></th>
                  </tr>
                </thead>
                <tbody>
                  {orderSort.sortedRows.length === 0 ? (
                    <tr><td colSpan="13" className="text-center text-slate-400 py-8">No hay productos de este proveedor que necesiten reposición para {proposal.horizon_days} días.</td></tr>
                  ) : orderSort.sortedRows.map((it, i) => (
                    <tr key={`o-${it.cn}`} data-testid={`order-row-${it.cn}`}>
                      <td className="font-mono">{it.cn}</td>
                      <td>{it.descripcion}</td>
                      <td className="text-xs">
                        <div className="font-semibold text-slate-700">{it.familia || "—"}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                          <span><b>{it.family_total || 0}</b> productos</span>
                          {(it.family_parado_count || 0) > 0 && (
                            <button
                              onClick={() => openFamily(it.familia)}
                              className="text-amber-700 hover:text-amber-900 hover:underline font-semibold"
                              data-testid={`open-family-${it.cn}`}
                              title="Ver TODOS los parados de esta familia para canje"
                            >
                              · <b>{it.family_parado_count}</b> parados →
                            </button>
                          )}
                        </div>
                        {it.substitutes?.length ? (
                          <details className="cursor-pointer mt-1">
                            <summary className="text-[10px] text-amber-700 font-semibold flex items-center gap-1"><RefreshCcw size={10}/> Top {it.substitutes.length} sustitutos</summary>
                            <ul className="mt-1 text-[10px] text-slate-600 max-w-md">
                              {it.substitutes.map((s) => (
                                <li key={s.cn} className="border-b border-slate-100 py-0.5">
                                  <span className="font-mono">{s.cn}</span> · {s.descripcion} · stock {s.stock} · <span className={`abcd-pill abcd-${s.abcd}`}>{s.abcd}</span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        ) : null}
                      </td>
                      <td><span className={`abcd-pill abcd-${it.abcd}`}>{it.abcd}</span></td>
                      <td className="num">{it.stock}</td>
                      <td className="num">{it.uds_periodo ?? "—"}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(it.ultima_venta)}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(it.ultima_entrada)}</td>
                      <td className="num">{it.venta_diaria.toFixed(2)}</td>
                      <td className="num">{it.objetivo}</td>
                      <td className="num">
                        <input type="number" min={0} value={it.pedido} data-testid={`order-pedido-${it.cn}`}
                               onChange={(e) => { const v = Math.max(0, parseInt(e.target.value, 10) || 0); setAjPed((a) => ({ ...a, [it.cn]: v })); }}
                               className={"w-14 text-right font-bold rounded border px-1.5 py-0.5 " + (it.pedido !== it.propuesto ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200")} />
                      </td>
                      <td className="num">{(it.pmc || 0).toFixed(2)}</td>
                      <td className="num font-bold">{(it.neto || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* INFORME DE CANJE */}
          <div className="card border-2 border-amber-300">
            <div className="px-5 py-4 border-b border-amber-100 bg-amber-50/50 flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="font-display text-lg font-bold flex items-center gap-2">
                  <RefreshCcw size={18} className="text-amber-600"/> Informe de canje · Stock parado (CC + D) de {supplier.name}
                </h2>
                <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                  Estos productos del proveedor están <b>parados</b> en tu farmacia (clase CC = trimestral, clase D = sin ventas).
                  Úsalos como <b>moneda de canje</b>: pídele al comercial que te los compense contra el pedido.
                  Total disponible: <b className="text-amber-700">{formatMoney(proposal.parado.total_value_pvp)}</b> a PVP.
                </p>
              </div>
              <ExportButtons
                filename={`canje-${supplier.name}`}
                title={`Stock parado para canje · ${supplier.name}`}
                subtitle={`${proposal.parado.lines} ref. · ${formatMoney(proposal.parado.total_value_pvp)} a PVP`}
                sheetName="Canje"
                columns={[
                  { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                  { header: "Familia", key: "familia" }, { header: "ABCD", key: "abcd" },
                  { header: "Stock", key: "stock", align: "right" },
                  { header: "Valor PVP", key: "valor_pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
                  { header: "Última venta", key: "ultima_venta", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Última entrada", key: "ultima_entrada", format: (v) => (v ? fmtD(v) : "") },
                ]}
                rows={paradoSort.sortedRows}
              />
            </div>
            <div className="overflow-x-auto">
              <table className="dense text-[11px]" data-testid="parado-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={paradoSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={paradoSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Familia" k="familia" sort={paradoSort} defaultDir="asc"/></th>
                    <th><SortHeader label="ABCD" k="abcd" sort={paradoSort} defaultDir="asc"/></th>
                    <th className="num"><SortHeader label="Stock" k="stock" sort={paradoSort} num/></th>
                    <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={paradoSort} num/></th>
                    <th className="num"><SortHeader label="Uds vend." k="uds_vendidas" sort={paradoSort} num/></th>
                    <th><SortHeader label="Últ. venta" k="ultima_venta" sort={paradoSort} defaultDir="desc"/></th>
                    <th><SortHeader label="Últ. entrada" k="ultima_entrada" sort={paradoSort} defaultDir="desc"/></th>
                  </tr>
                </thead>
                <tbody>
                  {paradoSort.sortedRows.length === 0 ? (
                    <tr><td colSpan="9" className="text-center text-slate-400 py-8">No hay stock parado (CC ni D) de este proveedor. ¡Buena gestión!</td></tr>
                  ) : paradoSort.sortedRows.map((p, i) => (
                    <tr key={`pd-${i}-${p.cn}`}>
                      <td className="font-mono">{p.cn}</td>
                      <td>{p.descripcion}</td>
                      <td className="text-xs">{p.familia}</td>
                      <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                      <td className="num">{p.stock}</td>
                      <td className="num font-bold">{(p.valor_pvp || 0).toFixed(2)}</td>
                      <td className="num">{p.uds_vendidas ?? 0}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_venta)}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_entrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Slide-over panel: TODOS los parados de una familia */}
      {famPanel && (
        <div className="fixed inset-0 z-[60] flex" data-testid="family-panel">
          <div className="w-full bg-white shadow-2xl flex flex-col h-full">
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4 bg-amber-50">
              <VolverButton onClick={famNav.back} testid="family-panel-volver" className="mt-1" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Moneda de canje · Familia completa</div>
                <h2 className="font-display text-xl font-extrabold mt-1">{famPanel.familia}</h2>
                {famLoading ? (
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Loader2 size={11} className="animate-spin"/> Cargando…</div>
                ) : (
                  <div className="text-xs text-slate-700 mt-1">
                    <b>{famPanel.count}</b> referencias paradas (CC + D) · valor total a PVP:{" "}
                    <b className="text-amber-700">{formatMoney(famPanel.total_value_pvp)}</b>
                  </div>
                )}
              </div>
              <button onClick={famNav.close} className="p-2 hover:bg-amber-100 rounded-lg" data-testid="close-family-panel">
                <X size={18}/>
              </button>
            </div>
            <div className="px-6 py-3 border-b border-slate-200">
              <ExportButtons
                filename={`canje-familia-${famPanel.familia}`.replace(/[^a-z0-9-]/gi, "_")}
                title={`Stock parado · Familia ${famPanel.familia}`}
                subtitle={`${famPanel.count} ref. · ${formatMoney(famPanel.total_value_pvp)} a PVP`}
                sheetName="Familia"
                columns={[
                  { header: "CN", key: "cn" }, { header: "Descripción", key: "descripcion" },
                  { header: "Laboratorio", key: "laboratorio" },
                  { header: "ABCD", key: "abcd" },
                  { header: "Stock", key: "stock", align: "right" },
                  { header: "PVP", key: "pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Valor PVP", key: "valor_pvp", format: (v) => (v||0).toFixed(2), align: "right" },
                  { header: "Uds vendidas", key: "uds_vendidas", align: "right" },
                  { header: "Última venta", key: "ultima_venta", format: (v) => (v ? fmtD(v) : "") },
                  { header: "Última entrada", key: "ultima_entrada", format: (v) => (v ? fmtD(v) : "") },
                ]}
                rows={famSort.sortedRows}
              />
            </div>
            <div className="flex-1 overflow-auto">
              <table className="dense text-[11px]" data-testid="family-table">
                <thead>
                  <tr>
                    <th><SortHeader label="CN" k="cn" sort={famSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Descripción" k="descripcion" sort={famSort} defaultDir="asc"/></th>
                    <th><SortHeader label="Lab" k="laboratorio" sort={famSort} defaultDir="asc"/></th>
                    <th><SortHeader label="ABCD" k="abcd" sort={famSort} defaultDir="asc"/></th>
                    <th className="num"><SortHeader label="Stock" k="stock" sort={famSort} num/></th>
                    <th className="num"><SortHeader label="Valor PVP" k="valor_pvp" sort={famSort} num/></th>
                    <th className="num"><SortHeader label="Uds vend." k="uds_vendidas" sort={famSort} num/></th>
                    <th><SortHeader label="Últ. venta" k="ultima_venta" sort={famSort} defaultDir="desc"/></th>
                    <th><SortHeader label="Últ. entrada" k="ultima_entrada" sort={famSort} defaultDir="desc"/></th>
                  </tr>
                </thead>
                <tbody>
                  {famLoading ? (
                    <tr><td colSpan="9" className="text-center text-slate-400 py-8"><Loader2 size={14} className="inline animate-spin mr-2"/> Cargando…</td></tr>
                  ) : famSort.sortedRows.length === 0 ? (
                    <tr><td colSpan="9" className="text-center text-slate-400 py-8">Sin parados en esta familia.</td></tr>
                  ) : famSort.sortedRows.map((p) => (
                    <tr key={`fp-${p.cn}`}>
                      <td className="font-mono">{p.cn}</td>
                      <td>{p.descripcion}</td>
                      <td className="text-xs">{p.laboratorio || "—"}</td>
                      <td><span className={`abcd-pill abcd-${p.abcd}`}>{p.abcd}</span></td>
                      <td className="num">{p.stock}</td>
                      <td className="num font-bold">{(p.valor_pvp || 0).toFixed(2)}</td>
                      <td className="num">{p.uds_vendidas ?? 0}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_venta)}</td>
                      <td className="font-mono whitespace-nowrap">{fmtD(p.ultima_entrada)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
